import React from "react";
import { useSelector } from "react-redux";
import {
    Button,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import InfoIcon from "@material-ui/icons/Info";
import { ApproveDialog, ApprovedTabsEnum, RejectDialog } from "./ApprovedAgentsPage";
import { ApprovedDetails } from "./index";
import { SearchTextField } from "../agent/ClientPage";
import { NiwSelect } from "../imo/ImoTab";
import { Loader, TableView } from "../../components/Common";
import { RootState } from "../../reducers";
import { useActions } from "../../actions";
import * as ApprovedAgentActions from "../../actions/approvedAgents";
import { ImoClass, imoRegionDropDown, IrrStatus } from "../../constants";
import { joinAsFullName, maskPhoneNumber, sleep } from "../../utils/commonUtils";
import { asDbDateTimeFormat } from "../../utils/dateUtils";
import { updatePendingCounts } from "../../actions/notification";
import * as AccountActions from "../../actions/account";
import { Dialog as GenericDialog, GenericDialogProps } from "../../components/dialog";
import LoaderGif from "../../styles/images/loader_nw.gif";
import { Button as CustomMuiButton } from "../../components/formInputs";
import * as DocusignActions from "../../actions/docusign";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";

interface AGTabProps {
    onAction: Function;
    status: number;
    search: string;
    setSearch: Function;
    region: string;
    setRegion: Function;
}

type OPN = { icon?: any; id: number; name: string; }

const AATable: React.FC<AGTabProps> = (props) => {
    const { onAction, status, region, search, setRegion, setSearch } = props;
    const accountActions = useActions(AccountActions);
    const approvedAgentActions = useActions(ApprovedAgentActions);
    const docusignActions = useActions(DocusignActions);
    const { user } = useSelector((state: RootState) => state.auth);
    const approvedDetail = useSelector((state: RootState) => state.approvedDetail);
    const registrationRequestList = useSelector((state: RootState) => state.approvedList);
    const noOption = {id: 0, name: 'Select option'};
    const showRejectionReason = status === ApprovedTabsEnum.Deleted;
    const initFalse = false;
    const [loading, setLoading] = React.useState(false);
    const [refreshing, setRefreshing] = React.useState(false);
    const [selectedAction, setSelectedAction] = React.useState<any>(noOption.name);
    const [selectedActionID, setSelectedActionID] = React.useState<any>(noOption.id);
    const [isApprove, setIsApprove] = React.useState<boolean>(false);
    const [isApproveDialog, setIsApproveDialog] = React.useState<boolean>(false);
    const [isReject, setIsReject] = React.useState<boolean>(false);
    const [sellerAgreementLoading, setSellerAgreementLoading] = React.useState(false);
    const [sellerAgreement, setSellerAgreement] = React.useState<boolean>(initFalse);
    const [sellerAgreementUrl, setSellerAgreementUrl] = React.useState('');
    const checkButtonInteractivity = (approvedDetail && approvedDetail.agent && approvedDetail.agent.sellerAgreements.length > 0 && approvedDetail.agent.sellerAgreements[0].approvedBy) !== "";
    const [buttonInteractivity, setButtonInteractivity] = React.useState(false);

    const loadApprovedList = async () => {
        setLoading(true);
        await approvedAgentActions.getAgentRegistrationRequests(status, search, region);
        setLoading(false);
    };
    const checkIfIMOApproved = (request: any) => {
        return request.agent ? request.agent.agentImos.map(x => x && x.imo && x.imo.imoClass)
                .filter(imoClass => imoClass === ImoClass.APPROVED || imoClass === ImoClass.PREFERRED)
                .length > 0 : false;
    }
    const refreshDetails = async (id: number) => {
        setRefreshing(true);
        await approvedAgentActions.getDetails(id);
        setRefreshing(false);
    };
    const getHeaders = () => {
        const _headers = [
            'Agent Name',
            'Email',
            'Work Phone',
            'IMO',
        ];
        if (status !== ApprovedTabsEnum.Pending) {
            _headers.push(showRejectionReason ? 'Deleted By' : 'Approved By');
        }
        _headers.push('Region');
        if (status === ApprovedTabsEnum.Approved) {
            _headers.push("Selling Agreement");
        }
        _headers.push('Date Received');
        if (showRejectionReason) {
            _headers.push('Deleted On');
            _headers.push('Deletion Reason');
        }
        if (status === ApprovedTabsEnum.Pending) {
            _headers.push('Actions');
        }
        return _headers;
    };
    const updateNotificationAlerts = async (payload: any) => {
        let pendingCounts = await updatePendingCounts(0, payload); // Update All action Item 0
        if (pendingCounts) {
            await accountActions.getFreshDataToRefreshReduxState();
        }
    };
    const onApproveAgent = async (id: number) => {
        setIsApproveDialog(true);
        setLoading(true);
        await approvedAgentActions.approveAgent(id);
        await refreshList();
        let agentApproval = (user && user.agentApproval) || 0;
        if (agentApproval > 0) {
            updateNotificationAlerts({ agentApproval: 0 });
        }
        return onClose();
    };
    const onRejectAgent = async (values: any, id: number, message: string = '') => {
        setLoading(true);
        await approvedAgentActions.rejectAgent(id, values.reason, message);
        await refreshList();
        let agentApproval = (user && user.agentApproval) || 0;
        if (agentApproval > 0) {
            updateNotificationAlerts({ agentApproval: 0 });
        }
        return onClose();
    };
    const onDirectRejectAgentViaIMOReject = async (values: {reason: string}) => {
        if (!values.reason || !approvedDetail.id) {
            return;
        }
        await sleep();
        await onRejectAgent(values, approvedDetail.id, "IMO & Agent deleted successfully.");
        return onClose();
    };
    const onClose = async () => {
        setIsApprove(false);
        setIsReject(false);
        setIsApproveDialog(initFalse);
        setSelectedAction(noOption.name);
        setSelectedActionID(noOption.id);
        await approvedAgentActions.flushApprovedDetails();
        setLoading(false);
    };
    const refreshList = async () => {
        await loadApprovedList();
        onAction();
    }
    const imoRegions = [
        {
            id: 0,
            value: 'all',
            name: 'All'
        },
        ...imoRegionDropDown
    ];
    const headers = getHeaders();
    const renderApproveList = (request: any) => {
        const isRejected = request.status === IrrStatus.REJECTED;
        const canBeApprove = !isRejected;
        const sellerAgreementsId = request && request.agent && request.agent.sellerAgreements.length > 0 && request.agent.sellerAgreements[0].id;
        const getOptns = () => {
            const approveOption = {
                icon: () => <CheckCircleIcon className="textHighLight floatLeft mr10" />,
                id: 1,
                name: 'Approve',
            };
            let optns: OPN[] = [];
            switch (status) {
                case ApprovedTabsEnum.Pending:
                    optns = [
                        noOption,
                        canBeApprove ? approveOption : {} as OPN,
                        {icon: () => <HighlightOffIcon className="colorRed floatLeft mr10" />, id: 2, name: 'Reject'},
                    ];
                    break;
            }
            return optns;
        }
        const options = getOptns();
        const handleSelect = async (e: any, id: number) => {
            const selectedOpt = e.target.value;
            setSelectedAction(selectedOpt);
            setSelectedActionID(id);

            if (selectedOpt === 'Approve') {
                setButtonInteractivity(checkButtonInteractivity);
                setIsApprove(true);
            }
            if (selectedOpt === 'Reject') {
                setIsReject(true);
            }

            await refreshDetails(id);
        };
        const approvedListItem = {
            name: (
                    <>
                        {
                                !isRejected &&
                                <Link onClick={() => {
                                    refreshDetails(request.id);
                                    setIsApprove(true);
                                }}>{request.agentName}</Link>
                        }
                        {
                                isRejected && request.agentName
                        }
                        {
                                !canBeApprove && status === ApprovedTabsEnum.Pending &&
                                <Tooltip title="Agent must be tied to at least one approved or preferred IMO to approve agent.">
                                    <IconButton aria-label="info">
                                        <InfoIcon color="primary" />
                                    </IconButton>
                                </Tooltip>
                        }
                    </>
            ),
            email: request.agentEmail,
            workPhone: maskPhoneNumber(request.agentPhone),
            imo: request.imoName,
        };

        if (status !== ApprovedTabsEnum.Pending) {
            Object.assign(approvedListItem, {
                actionBy: request.actionUser && joinAsFullName(request.actionUser),
            });
        }

        Object.assign(approvedListItem, {
            region: request.region,
        });

        if (status === ApprovedTabsEnum.Approved) {
            Object.assign(approvedListItem, {
                sellingAgreement: (
                        <>
                            {
                                    sellerAgreementsId &&
                                    <>
                                        <Grid container justify="center" spacing={2}>
                                            <Grid item xs={12} md={12} className="textCenter">
                                                <VisibilityOutlinedIcon className="textHighLight"
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => {
                                                                            loadSellerAgreement(request.agentId, sellerAgreementsId);
                                                                        }} />
                                            </Grid>
                                        </Grid>
                                    </>
                            }
                        </>
                ),
            });
        }

        Object.assign(approvedListItem, {
            dateReceived: asDbDateTimeFormat(request.createdAt),
        });

        if (status === ApprovedTabsEnum.Deleted) {
            Object.assign(approvedListItem, {dateDeletedAt: request.deletedAt ? asDbDateTimeFormat(request.deletedAt) : ''})
        }

        if (showRejectionReason) {
            Object.assign(approvedListItem, {rejectionReason: request.rejectionReason})
        }

        if (status === ApprovedTabsEnum.Pending && options.length > 0) {
            Object.assign(approvedListItem, {
                action: (
                        <NiwSelect
                                onChange={e => handleSelect(e, request.id)}
                                options={options}
                                selectedOption={selectedAction}
                        />
                )
            })
        }

        return approvedListItem;
    };
    const gridProps = {
        headers: headers,
        list: registrationRequestList.map(renderApproveList)
    };

    React.useEffect(() => {
        loadApprovedList()
                .then();

        return () => approvedAgentActions.flushApprovedList();
    }, [search, region]);

    React.useEffect(() => {
        (
                async () => {
                    if (selectedActionID) {
                        await refreshDetails(selectedActionID);
                    }
                }
        )();
    }, [selectedActionID, isApprove, sellerAgreement]);

    const loadSellerAgreement = async (agentId: number, sellerAgreementId: number) => {
        setSellerAgreement(!initFalse);
        setSellerAgreementLoading(!initFalse);
        let pageUrl = window.location.protocol + "//" + window.location.hostname;
        if (window.location.hostname === "localhost") {
            pageUrl = pageUrl + ":" + window.location.port;
        }
        pageUrl += `/new-client-detail/thanks?isAdminApproveSellerAgreement=1&sellerAgreementId=${sellerAgreementId}`;
        const res = await docusignActions.getSellerAgreement(agentId, pageUrl);
        setSellerAgreementLoading(initFalse);
        setSellerAgreementUrl(res.envelopeUrl);
    };

    const sellerAgreementClose = async () => {
        setSellerAgreement(initFalse);
        setSellerAgreementLoading(initFalse);
        setButtonInteractivity(true);
        return setSellerAgreementUrl('');
    };

    const sellerCloseButtonProps = {
        id: "adminclosebtn",
        label: "Close",
        onClick: sellerAgreementClose,
        style: { display: "none" },
    };
    const sellerDialogProps: GenericDialogProps = {
        actions: (
                <Button {...sellerCloseButtonProps} />
        ),
        "aria-describedby": "seller-agreement-dialog",
        content: (
                <>
                    <div id="seller-agreement-dialog">
                        {
                                sellerAgreementLoading &&
                                <div className="mt30 textCenter">
                                    <img alt="Loading..." className="mb15" src={LoaderGif}/>
                                    <br/>
                                    <Typography variant="caption" className="w100">
                                        Signing document is being prepared. This could take some time so please be
                                        patient and don\'t close this window.
                                    </Typography>
                                </div>
                        }
                        <iframe
                                src={sellerAgreementUrl}
                                style={{width: "100%", height: "80vh"}}
                                title="seller-agreement-dialog"
                        />
                    </div>
                    <CustomMuiButton
                            id="viewer_iframe_confirm"
                            label="Confirm"
                            onClick={sellerAgreementClose}
                            style={{display: "none"}}
                    />
                </>
        ),
        fullWidth: !initFalse,
        keepMounted: !initFalse,
        maxWidth: "md",
        onClose: sellerAgreementClose,
        open: sellerAgreement,
        title: 'Approved Agent Selling Agreement',
    }

    return (
            <>
                {
                        loading && <Loader />
                }
                <Grid container spacing={3} justify="space-between">
                    <Grid item xs={12} md={12} lg={6}>
                        <Typography variant="body1">
                            This is a list of all new leads that have entered the system. These
                            leads must be assigned to an agent before they can be invited to myilia.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={12} lg={3}>
                        <FormControl variant="outlined" fullWidth={true}>
                            <InputLabel htmlFor="imo-type" shrink>Region</InputLabel>
                            <Select className="muiSelect" value={region} onChange={e => setRegion(e.target.value as string)} fullWidth>
                                {
                                    imoRegions.map((o) =>
                                            <MenuItem key={o.id} value={o.value}>
                                                {o.name}
                                            </MenuItem>)
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={12} lg={3}>
                        <SearchTextField search={search} label={'Search Agents and IMO'} onChange={(e) => setSearch(e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                        <TableView {...gridProps} />
                    </Grid>
                    <Grid item xs={12}>
                        {
                                isApproveDialog &&
                                approvedDetail &&
                                <ApproveDialog
                                        description="Are you sure you want to approve this agent? Have you verified they work with the IMO's they have requested during registration?"
                                        handleClose={onClose}
                                        onApprove={() => onApproveAgent(approvedDetail.id)}
                                        open
                                        title={`Approve ${joinAsFullName(approvedDetail.agent && approvedDetail.agent.user)}`}
                                />
                        }
                        {
                                isReject &&
                                approvedDetail &&
                                <RejectDialog
                                        description="Let this agent know why they were rejected. They will receive an email with this reason."
                                        handleClose={onClose}
                                        id={approvedDetail.id}
                                        onReject={onRejectAgent}
                                        open
                                        title={`Reject ${joinAsFullName(approvedDetail.agent && approvedDetail.agent.user)}`}
                                        refreshing={refreshing}
                                />
                        }
                        {
                                isApprove && approvedDetail &&
                                <ApprovedDetails
                                        checkIfIMOApproved={checkIfIMOApproved}
                                        handleClose={onClose}
                                        onApproveAgent={() => onApproveAgent}
                                        buttonInteractivity={buttonInteractivity}
                                        setButtonInteractivity={setButtonInteractivity}
                                        setIsApproveDialog={setIsApproveDialog}
                                        viewSellingAgreement={loadSellerAgreement}
                                        onRejectAgent={() => setIsReject(true)}
                                        onDirectRejectAgent={onDirectRejectAgentViaIMOReject}
                                        refresh={refreshList}
                                        status={status}
                                />
                        }
                        {
                                sellerAgreement &&
                                <GenericDialog {...sellerDialogProps} />
                        }
                    </Grid>
                </Grid>
            </>
    );
};

export default AATable;
